import type { IPublicClientApplication, SilentRequest } from '@azure/msal-browser';

/**
 * Acquire an Azure AD token silently. On any of the "needs interactive
 * login" errors we fall back to `acquireTokenRedirect` — which kicks the page
 * through the login flow. Mirrors the pattern in
 * `sfc-shared/src/auth/tokenHelper.ts`.
 *
 * We return the **id_token**, not the access_token, so dashboard-api can
 * verify it with the tenant's public JWKS. Microsoft Graph access tokens
 * (what `User.Read` yields) are deliberately unverifiable by relying
 * parties — the signing keys aren't in the tenant JWKS. The id_token has
 * `aud = our client id` and `iss = https://login.microsoftonline.com/<tid>/v2.0`,
 * which matches the Worker's verifier exactly.
 */

const REDIRECT_ERROR_CODES = new Set([
  'interaction_required',
  'consent_required',
  'login_required',
  'no_tokens_found',
  'no_account_error',
  'token_refresh_required',
  'user_cancelled',
  'monitor_window_timeout',
  'popup_window_error',
  'empty_navigate_uri',
  'hash_empty_error',
  'interaction_in_progress',
  'no_cached_authority_error',
  'no_account_in_silent_request',
  'multiple_matching_tokens',
  'invalid_client_credential',
  'invalid_grant',
  'expired_token',
  'invalid_token',
]);

function requiresInteractive(err: unknown): boolean {
  const e = err as { errorCode?: string; message?: string };
  const code = e?.errorCode?.toLowerCase();
  if (code && REDIRECT_ERROR_CODES.has(code)) return true;
  const msg = e?.message?.toLowerCase() ?? '';
  return (
    msg.includes('interaction_required') ||
    msg.includes('consent_required') ||
    msg.includes('login_required')
  );
}

export interface TokenResult {
  /**
   * Id token — the Worker uses this as the Bearer token because it's
   * verifiable against the tenant JWKS. Field name kept as `accessToken`
   * so existing call-sites don't need to change.
   */
  accessToken: string;
}

export async function acquireToken(
  instance: IPublicClientApplication,
  request: SilentRequest,
): Promise<TokenResult> {
  try {
    const result = await instance.acquireTokenSilent(request);
    return { accessToken: result.idToken };
  } catch (err) {
    if (requiresInteractive(err)) {
      await instance.acquireTokenRedirect(request);
      // acquireTokenRedirect returns void — the page navigates away and on
      // return MsalProvider picks up the result. Throw so callers know they
      // can't continue this async flow.
      throw new Error('Redirecting to interactive login');
    }
    throw err;
  }
}
